/**
 * The All table's settings (D-247, W8-03-S14-T4): what the URL keeps, what the
 * server accepts, and how a "When" pick becomes the server's from/to.
 *
 * The lists mirror the server's own (router_messaging.py InboxSort, InboxDir,
 * InboxStatus, InboxSentBy); a value off the list is dropped, never sent, so a
 * pasted or stale link never earns a 422.
 *
 * Kept small and free of React: `index.ts` (the eager route table) imports it.
 */

export const TABLE_SORTS = ["last_text", "customer", "status", "sent_by"] as const;
export const TABLE_DIRS = ["asc", "desc"] as const;
export const TABLE_STATUSES = ["delivered", "sent", "undelivered", "refused", "failed", "waiting"] as const;
export const TABLE_SENDERS = ["customer", "customer_connect", "person", "agent", "system"] as const;
export const TABLE_WHENS = ["today", "week", "month", "custom"] as const;
/** The server answers 422 above 20 error codes (W8-03-S14-T3 fix round 1). */
export const MAX_ERROR_CODES = 20;

export type TableSort = (typeof TABLE_SORTS)[number];
export type TableDir = (typeof TABLE_DIRS)[number];
export type TableStatus = (typeof TABLE_STATUSES)[number];
export type TableSender = (typeof TABLE_SENDERS)[number];
export type TableWhen = (typeof TABLE_WHENS)[number];

/** Everything the person sets on the All table; all of it lives in the URL. */
export interface TableQuery {
  sort?: TableSort | undefined;
  dir?: TableDir | undefined;
  status?: string[] | undefined;
  sent_by?: string[] | undefined;
  error?: string[] | undefined;
  when?: TableWhen | undefined;
  /** `YYYY-MM-DD`, the viewer's own calendar day (When = custom). */
  from?: string | undefined;
  to?: string | undefined;
}

export const TABLE_QUERY_KEYS = ["sort", "dir", "status", "sent_by", "error", "when", "from", "to"] as const;

function oneOf<T extends string>(list: readonly T[], v: unknown): T | undefined {
  return typeof v === "string" && (list as readonly string[]).includes(v) ? (v as T) : undefined;
}

function listOf(v: unknown, keep: (s: string) => boolean, max = Infinity): string[] | undefined {
  const raw = Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item === "string" && keep(item) && !out.includes(item)) out.push(item);
  }
  return out.length > 0 && out.length <= max ? out : undefined;
}

/** A real calendar day written `YYYY-MM-DD` (so 2026-13-45 is refused). */
export function isDay(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** An error code as the server words it: a carrier number or a word code. */
const ERROR_CODE = /^[A-Za-z0-9_.-]{1,64}$/;

/** T4.1: keep every valid setting, drop every invalid one, keep the rest of a list. */
export function readTableQuery(raw: Record<string, unknown>): TableQuery {
  const sort = oneOf(TABLE_SORTS, raw["sort"]);
  const dir = oneOf(TABLE_DIRS, raw["dir"]);
  const status = listOf(raw["status"], (s) => (TABLE_STATUSES as readonly string[]).includes(s));
  const sent_by = listOf(raw["sent_by"], (s) => (TABLE_SENDERS as readonly string[]).includes(s));
  const error = listOf(raw["error"], (s) => ERROR_CODE.test(s), MAX_ERROR_CODES);
  const when = oneOf(TABLE_WHENS, raw["when"]);
  const from = isDay(raw["from"]) ? raw["from"] : undefined;
  const to = isDay(raw["to"]) ? raw["to"] : undefined;
  return {
    ...(sort ? { sort } : {}),
    ...(dir ? { dir } : {}),
    ...(status ? { status } : {}),
    ...(sent_by ? { sent_by } : {}),
    ...(error ? { error } : {}),
    ...(when ? { when } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
}

/** Is any filter (not the sort) set? */
export function hasTableFilters(q: TableQuery): boolean {
  return !!(q.status?.length || q.sent_by?.length || q.error?.length || q.when || q.from || q.to);
}

// whenRange lives in ./table-range.ts: only the inbox screen (loaded on
// demand) needs it, and this file is in every visitor's first download.
